/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import moment from 'moment';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme, useThemeMode} from '@ion/dls/src/theme/useThemeContext';

export const useChartOptions = (preferredTimeFormat) => {
  const isCurrent = isCurrentTheme();
  // the theme mode re-resolves the chart's colors below on a light/dark switch ...
  const {mode}    = useThemeMode();

  const yScale = useMemo(() => ({
    type    : 'linear',
    min     : 'auto',
    max     : 'auto',
    stacked : false,
    reverse : false
  }), []);

  const axisLeft = useMemo(() => ({
    tickSize     : 10,
    tickPadding  : 10,
    tickRotation : 0,
    tickValues   : 10
  }), []);

  const xScale = useMemo(() => ({
    type      : 'time',
    format    : '%Y-%m-%dT%H:%M:%S.%L%Z',
    precision : 'minute'
  }), []);

  const axisBottom = useMemo(() => ({
    tickValues  : 'every 2 hour',
    tickSize    : 10,
    format      : (data) => moment(data).format(preferredTimeFormat),
    tickPadding : 0
  }), [preferredTimeFormat]);

  const legendTheme = useMemo(() => ({
    legend : {
      text : {
        fontSize : 14,
        color    : dlsColor('grey3')
      }
    }
  }), [mode]);

  // current theme's Inter typography for the chart's axis tick labels and
  // legend text - merged over the legacy legendTheme above rather than
  // edited in place, so legacy keeps its own untouched object ...
  const currentLegendTheme = useMemo(() => ({
    ...legendTheme,
    legend : {
      text : {
        ...legendTheme.legend.text,
        fontFamily : 'Inter'
      }
    },
    ticks : {
      text : {
        fontFamily : 'Inter',
        fill       : dlsColor('grey3')
      }
    }
  }), [legendTheme, mode]);

  const domainTheme = useMemo(() => ({
    domain : {
      line : {
        stroke      : dlsColor('grey4'),
        strokeWidth : 1
      }
    }
  }), [mode]);

  return useMemo(() => ({
    yScale,
    axisLeft,
    xScale,
    axisBottom,
    legendTheme : isCurrent ? currentLegendTheme : legendTheme,
    domainTheme
  }), [
    yScale,
    axisLeft,
    xScale,
    axisBottom,
    legendTheme,
    currentLegendTheme,
    domainTheme,
    isCurrent
  ]);
};

export default useChartOptions;
